import type { GtmDemoRunResponse, GtmDemoRunStep } from '@laplace/contracts'
import type { GtmDemoErrorKey } from '@/features/gtm/api/gtm-demo-api'
import { GtmDemoRequestError } from '@/features/gtm/api/gtm-demo-api'

/** Progress steps shown to the visitor, in pipeline order. */
export const GTM_DEMO_STEPS = [
  'research_company',
  'finding_leads',
  'writing_emails',
] as const satisfies ReadonlyArray<GtmDemoRunStep>

export type GtmDemoPhase = 'idle' | 'running' | 'completed' | 'failed'

export interface GtmDemoView {
  phase: GtmDemoPhase
  /** Index into GTM_DEMO_STEPS of the step in progress. */
  activeStepIndex: number
  run: GtmDemoRunResponse | null
  errorKey: GtmDemoErrorKey | null
}

export function isGtmDemoRunInProgress(
  run: GtmDemoRunResponse | undefined,
): boolean {
  return run?.status === 'queued' || run?.status === 'running'
}

function stepIndex(step: GtmDemoRunStep | null): number {
  if (step === null) return 0
  if (step === 'done') return GTM_DEMO_STEPS.length - 1
  // Steps this compact view does not list (competitors, or ones from a newer
  // API) count as the first one.
  const steps: ReadonlyArray<GtmDemoRunStep> = GTM_DEMO_STEPS
  return Math.max(steps.indexOf(step), 0)
}

function errorKeyOf(error: unknown): GtmDemoErrorKey {
  return error instanceof GtmDemoRequestError ? error.key : 'internal'
}

export interface ResolveGtmDemoViewInput {
  isStarting: boolean
  startError: unknown
  runId: string | undefined
  run: GtmDemoRunResponse | undefined
  runError: unknown
}

/** Collapses mutation + polling query state into what the page renders. */
export function resolveGtmDemoView({
  isStarting,
  startError,
  runId,
  run,
  runError,
}: ResolveGtmDemoViewInput): GtmDemoView {
  if (startError) {
    return {
      phase: 'failed',
      activeStepIndex: 0,
      run: null,
      errorKey: errorKeyOf(startError),
    }
  }
  if (isStarting) {
    return { phase: 'running', activeStepIndex: 0, run: null, errorKey: null }
  }
  if (!runId) {
    return { phase: 'idle', activeStepIndex: 0, run: null, errorKey: null }
  }
  if (runError) {
    return {
      phase: 'failed',
      activeStepIndex: 0,
      run: null,
      errorKey: errorKeyOf(runError),
    }
  }
  if (!run) {
    return { phase: 'running', activeStepIndex: 0, run: null, errorKey: null }
  }
  if (run.status === 'completed') {
    return {
      phase: 'completed',
      activeStepIndex: GTM_DEMO_STEPS.length - 1,
      run,
      errorKey: null,
    }
  }
  if (run.status === 'failed') {
    return {
      phase: 'failed',
      activeStepIndex: stepIndex(run.step),
      run,
      errorKey: run.errorCode ?? 'internal',
    }
  }
  return {
    phase: 'running',
    activeStepIndex: stepIndex(run.step),
    run,
    errorKey: null,
  }
}
